import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Typography,
  FormControl,
  TextField,
  Grid,
  Box,
  Paper,
  Button,
  makeStyles,
  CircularProgress,
  Snackbar,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import axios from "axios";
import { commonInlineStyles, inputFieldStyles } from "../../styles";
import { appUrl } from "../../../../../../../utils/config";
import Alert from "@material-ui/lab/Alert";
import { useLocation } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: "100%",
  },
  label: {
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  textField: {
    width: "100%",
    backgroundColor: "#fff",
  },
  requiredField: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(0.5),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: 14,
    marginTop: theme.spacing(0.5),
  },
}));

const ShipmentConfigurations = ({
  formData,
  updateStepData,
  classes: parentClasses,
  readOnly = false,
  handleNext,
  handleBack,
  handleSubmit,
  activeStep,
  stepsLength,
  isShipmentConfigEditMode = false,
  markApiCallSuccessful,
  onValidationChange = () => { },
}) => {  // Initialize step data from formData or with default values
  const [stepData, setStepData] = useState(
    formData.shipmentConfiguration || {
      dispatchDuration: 7
    }
  );

  const location = useLocation()

  const viewOnlyProductDetails = location.state?.viewProductDetails;
  const [updateModeData, setUpdateModeData] = useState(null);

  // Track component mounted state
  const isMountedRef = useRef(true);

  // Handle component unmounting
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // API base URL - replace with your actual API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, [
    localStorage.getItem("appName"),
    localStorage.getItem("network"),
    localStorage.getItem("collabType"),
    localStorage.getItem("roleName"),
  ]);
  // Handle changes to form fields
  const handleChange = (event) => {
    const { name, value } = event.target;
    const safeValue = value || "";

    // Update local state
    const updatedStepData = {
      ...stepData,
      [name]: safeValue,
    };
    setStepData(updatedStepData);

    // Update parent component's formData
    if (typeof updateStepData === "function") {
      updateStepData("shipmentConfiguration", {
        ...updatedStepData,
        dispatchDuration: safeValue // Ensure dispatchDuration is updated
      });
    }

    // Validate field in real-time
    const newErrors = { ...errors };
    if (name === 'dispatchDuration') {
      if (!safeValue && safeValue !== 0) {
        newErrors.dispatchDuration = "Duration between ready for dispatch and dispatched (days) is required";
      } else if (isNaN(safeValue)) {
        newErrors.dispatchDuration = "Must be a number";
      } else if (Number(safeValue) < 0) {
        newErrors.dispatchDuration = "Value cannot be negative";
      } else if (!Number.isInteger(Number(safeValue))) {
        newErrors.dispatchDuration = "Value must be an integer";
      } else {
        delete newErrors.dispatchDuration;
      }
    }
    setErrors(newErrors);
  };

  // Update stepData when formData changes (like when navigating back to this step)
  useEffect(() => {
    if (formData && formData.shipmentConfiguration) {
      const duration = formData.shipmentConfiguration.dispatchDuration ??
        formData.shipmentConfiguration.dispatchDuration ??
        7;
      setStepData(prevData => ({
        ...prevData,
        dispatchDuration: duration
      }));
    }
  }, [formData]);

  // Add effect to handle edit mode data updates
  useEffect(() => {
    if (isShipmentConfigEditMode && formData?.productDetails?.collabrixProductID) {
      const fetchData = async () => {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/fetchProductDetails/${formData.productDetails.collabrixProductID}`
          );
          if (isMountedRef.current && response.data && response.data.status) {
            const apiData = response.data.data;
            setUpdateModeData(apiData);
            if (apiData.shipmentConfiguration) {
              const duration = apiData.shipmentConfiguration.dispatchDuration ??
                apiData.shipmentConfiguration.dispatchDuration ??
                null;
              setStepData(prevData => ({
                ...prevData,
                dispatchDuration: duration
              }));
              // Update parent formData
              if (typeof updateStepData === "function") {
                updateStepData("shipmentConfiguration", {
                  ...apiData.shipmentConfiguration,
                  dispatchDuration: duration
                });
              }
            }
          }
        } catch (error) {
          if (isMountedRef.current) {
            setSnackbar({
              open: true,
              message: "Failed to fetch product details.",
              severity: "error",
            });
          }
        }
      };
      fetchData();
    }
  }, [isShipmentConfigEditMode, formData?.productDetails?.collabrixProductID]);

  const classes = useStyles();

  // State for loading and error handling
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
    key: Date.now(),
  });

  // Handle closing snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  /**
   * Validates form fields before saving
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // Check dispatchDuration is a valid integer and not negative
    if (!stepData.dispatchDuration && stepData.dispatchDuration !== 0) {
      newErrors.dispatchDuration = "Duration between ready for dispatch and dispatched (days) is required";
      isValid = false;
    } else if (isNaN(stepData.dispatchDuration)) {
      newErrors.dispatchDuration = "Must be a number";
      isValid = false;
    } else if (Number(stepData.dispatchDuration) < 0) {
      newErrors.dispatchDuration = "Value cannot be negative";
      isValid = false;
    } else if (!Number.isInteger(Number(stepData.dispatchDuration))) {
      newErrors.dispatchDuration = "Value must be an integer";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Validation logic
  const validate = () => {
    let isValid = true;
    if (
      stepData.dispatchDuration === "" ||
      isNaN(stepData.dispatchDuration)
    ) isValid = false;
    return isValid;
  };

  // Call validation on every stepData change
  useEffect(() => {
    onValidationChange(validate());
  }, [stepData, onValidationChange]);

  /**
   * Saves shipment configurations to the API
   * Sends shipment configuration settings to the backend and navigates to the next step on success
   */
  const saveShipmentConfigurations = async () => {
    if (sessionStorage.getItem("productUpdated")) {
      sessionStorage.removeItem("productUpdated");
    }

    // Helper function to normalize shipment configuration for comparison
    function normalizeShipmentConfig(data) {
      return {
        dispatchDuration: Number(data.dispatchDuration || 0),
        // Add any other shipment configuration fields here if they're added in the future
      };
    }

    // Check if there are any changes before proceeding
    const newConfig = normalizeShipmentConfig(stepData);
    let oldConfig;

    if (isShipmentConfigEditMode && updateModeData?.shipmentConfiguration) {
      // When editing an existing product
      oldConfig = normalizeShipmentConfig(updateModeData.shipmentConfiguration);
    } else if (location.state?.addNewProductConfig && location.state?.selectedProductFormData?.shipmentConfiguration) {
      // When adding a new product based on an existing one
      oldConfig = normalizeShipmentConfig(location.state.selectedProductFormData.shipmentConfiguration);
    } else if (formData.shipmentConfiguration) {
      // When returning to this step after going forward/back
      oldConfig = normalizeShipmentConfig(formData.shipmentConfiguration);
    } else {
      // Default values for new product with no previous configuration
      oldConfig = normalizeShipmentConfig({});
    }

    const hasChanges = JSON.stringify(newConfig) !== JSON.stringify(oldConfig);

    // If no changes, skip validation and API call, just navigate to next step
    // if (!hasChanges) {

    //   handleNext();
    //   return;
    // }

    // Validate form sinceSnackbar there are changes
    if (!validateForm()) {
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: "Please correct the errors before proceeding.",
          severity: "error",
        });
      }
      return;
    }

    try {
      if (isMountedRef.current) {
        setIsLoading(true);
      }

      // Helper function to format productDetails for API
      const formatProductDetailsForAPI = (productDetails) => {
        const formatted = { ...productDetails };
        
        // Convert receivingStorageLocationIDsFromSponsorERP from array to comma-separated string
        if (formatted.receivingStorageLocationIDsFromSponsorERP) {
          if (Array.isArray(formatted.receivingStorageLocationIDsFromSponsorERP)) {
            formatted.receivingStorageLocationIDsFromSponsorERP = 
              formatted.receivingStorageLocationIDsFromSponsorERP.join(",");
          }
        }
        
        return formatted;
      };

      // Prepare the payload with the mapped field names
      const payload = {
        updateShipmentConfigFlag: true,
        productDetails: formatProductDetailsForAPI({
          ...formData.productDetails,
          collabrixProductID: formData.productDetails?.collabrixProductID,
          vendorMosymphonyID: formData.productDetails?.vendorMosymphonyID,
        }),
        shipmentConfiguration: {
          grnGenerationSettingID: 0, // Default to 0 as specified
          dispatchDuration: Number(stepData.dispatchDuration),
        },
      };

      // Make API call to save shipment configurations
      const response = await axios.post(
        `${apiBaseUrl}/updateProductDetails`,
        payload
      );

      // Mark API call as successful
      if (typeof markApiCallSuccessful === 'function' && response.data && response.data.status) {
        markApiCallSuccessful();
        return;
      }

      if (response.data && response.data.status) {
        // This section will run only if markApiCallSuccessful is not available
        handleNext();

        // Set success snackbar only if component is still mounted
        if (isMountedRef.current) {
          setSnackbar({
            open: true,
            message: "Shipment configurations saved successfully!",
            severity: "success",
          });
        }
      } else {
        // API returned non-success status - only update if mounted
        if (isMountedRef.current) {
          setSnackbar({
            open: true,
            message: "Failed to save shipment configurations. Please try again.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      // Handle any errors that occur during API call - only if mounted
      if (isMountedRef.current) {
        const errorMessage =
          "An error occurred while saving: " + (error.message || "Unknown error");
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };


  return (
    <form>
      <Paper elevation={0} className={classes.paper}>
        <Typography variant="h6">Batch Configurations</Typography>
        <Grid className="two-col-forms-grid" container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl className={classes.formControl}>
              <Typography
                component="label"
                htmlFor="dispatchDuration"
                className={classes.label}
              >
                Duration between ready for dispatch and dispatched (days){" "}
                {!readOnly && <span className={classes.requiredField}>*</span>}
              </Typography>
              {(!readOnly) ? (
                <>
                  <TextField
                    id="dispatchDuration"
                    value={stepData.dispatchDuration}
                    onChange={handleChange}
                    name="dispatchDuration"
                    variant="outlined"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, "");
                    }}
                    sx={inputFieldStyles}
                    inputProps={commonInlineStyles.inputProps}
                    style={commonInlineStyles.input}
                    className={classes.textField}
                    error={!!errors.dispatchDuration}
                    placeholder="Enter text here"
                  />
                  {errors.dispatchDuration && (
                    <Typography className={`error-text ${classes.errorText}`}>
                      {errors.dispatchDuration}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography>
                  {formData?.shipmentConfiguration?.dispatchDuration || stepData.dispatchDuration}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Navigation Buttons */}
      {(!readOnly && !viewOnlyProductDetails && !isShipmentConfigEditMode) &&
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
          }}
          className="stepper-navigation-btns"
          mt={4}
        >
          {activeStep !== 0 && (
            <>
              <Button
                variant="outlined"
                disabled={activeStep === 0}
                onClick={handleBack}
                className={classes && classes.button}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
            </>
          )}{" "}
          <Button
            variant="contained"
            color="primary"
            onClick={
              activeStep === stepsLength - 1
                ? handleSubmit
                : saveShipmentConfigurations
            }
            className={classes && classes.button}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : activeStep === stepsLength - 1 ? (
              "Save"
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      }

      {/* Error Snackbar */}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        style={{ zIndex: 9999 }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          //style={{ width: '100%' }}
          sx={{
            width: '100%',
            ...(snackbar.severity === 'error' && {
              backgroundColor: '#b71c1c',
              color: '#fff',
              fontWeight: 'bold'
            })
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </form>
  );
};

export default ShipmentConfigurations;
